// The engine→coach contract. ARCHITECTURE.md §5 (FactsDigest), ENGINE_DESIGN.md §8 (EngineFacts).
//
// EngineFacts is the FULL deterministic record (exact cards, equities, dollars, EV table) stored
// per decision in D1 and consumed by the replay/debrief UI + templates. FactsDigest is the bucketed
// PROJECTION that is the ONLY thing the LLM ever receives - no hole cards, no card strings, no raw
// inputs. Every FactsDigest field has a source field in EngineFacts.
import { z } from 'zod';
import { CardIndexSchema, BoardSchema } from './primitives';
import { PositionSchema, PositionClassSchema, StreetSchema } from './position';
import { ActionKindSchema, SizingIntentSchema } from './action';
import { ArchetypeIdSchema, ImbalanceProfileSchema, ImbalanceSummarySchema } from './archetype';
import { HandClassSchema, DrawFlagSchema, TextureClassSchema, BoardEventSchema } from './hand-class';
import { BucketedRangeSchema } from './range';
import { GameTypeSchema, ModeIdSchema } from './scenario';
import { EquityBucketSchema, DeltaBucketSchema, SprBucketSchema, EvGapBucketSchema, GradeBandSchema, IcmPressureSchema, ArithmeticVerdictSchema, SizingFitSchema, ConfidenceSchema, } from './buckets';
// ── shared leaf shapes ─────────────────────────────────────────────────────────
/** Equity computation result (ENGINE_DESIGN.md §5). */
export const EquityResultSchema = z.object({
    equity: z.number().min(0).max(1),
    ci95: z.number().nonnegative().optional(),
    win: z.number().min(0).max(1).optional(),
    tie: z.number().min(0).max(1).optional(),
});
/** Free-form label for a range class in a shape summary, e.g. "top pairs", "flush draws". Not part
 *  of the cache key, so a descriptive string is honest here. */
export const RangeClassLabelSchema = z.string();
export const PerceivedClassLabelSchema = z.string();
export const RangeShapeSchema = z.object({
    valueHeavyPct: z.number().min(0).max(100),
    bluffPct: z.number().min(0).max(100),
    topClasses: z.array(RangeClassLabelSchema),
});
/** A rule that fired, with its provenance (ENGINE_DESIGN.md §6.2). Digest carries {id,principle,confidence}. */
export const FiredRuleSchema = z.object({
    id: z.string(),
    principle: z.string(),
    villainNote: z.string().optional(),
    confidence: ConfidenceSchema,
    framework: z.string().optional(),
    /** True when the rule MATCHED but the resolver refused its `action.set` - e.g. an iso rule that says
     *  "raise" over a hand the chart folds (strategy/preflop.ts applySetAndForbid: junk is never turned into
     *  a raise). The rule stays in `firedRules` because matching IS the provenance the yardstick and the
     *  dimension attribution assert on, but its principle argues for an action the engine did NOT take, so no
     *  surface may render it as the explanation of the answer. */
    suppressed: z.boolean().optional(),
});
export const AlternativeSchema = z.object({
    action: ActionKindSchema,
    sizingIntent: SizingIntentSchema.optional(),
    /** EV in bb; absent when the model abstains (§7.2). */
    evBB: z.number().optional(),
    evGapBucket: EvGapBucketSchema,
});
/** The engine's recommendation. baselineAction + finalAction are both kept so the coach can teach the
 *  deviation (ENGINE_DESIGN.md §6.2); `action` mirrors finalAction for the digest projection. */
export const RecommendedActionSchema = z.object({
    baselineAction: ActionKindSchema,
    finalAction: ActionKindSchema,
    action: ActionKindSchema,
    sizingIntent: SizingIntentSchema.optional(),
    targetStatement: z.string().optional(),
    firedRules: z.array(FiredRuleSchema),
    alternatives: z.array(AlternativeSchema),
});
/** Session-scoped hero image tracker (spec §8; ENGINE_DESIGN.md §4.5). */
export const HeroImageStateSchema = z.object({
    handsObserved: z.number().int().nonnegative(),
    foldToAggressionPct: z.number().min(0).max(100).optional(),
    shownBluffs: z.number().int().nonnegative(),
    openFreqPct: z.number().min(0).max(100).optional(),
});
/** What-changed block computed per street (killer feature 1; ARCHITECTURE.md §9). */
export const WhatChangedSchema = z.object({
    equityDeltaBucket: DeltaBucketSchema.optional(),
    textureEvents: z.array(BoardEventSchema),
    rangeAdvantageShift: z.enum(['toHero', 'toVillain', 'unchanged']).optional(),
    lines: z.array(z.string()),
});
/** ICM facts block (ENGINE_DESIGN.md §6.5). */
export const IcmFactsSchema = z.object({
    pressure: IcmPressureSchema,
    riskPremium: z.number().optional(),
    icmEquity: z.array(z.number()).optional(),
});
/** The graded user action, including the typed dollar amount (§7). evLossBB is nullable: null when
 *  the model abstains from a precise EV ranking (§7.2). */
export const UserActionSchema = z.object({
    action: ActionKindSchema,
    sizingIntent: SizingIntentSchema.optional(),
    typedAmount: z.number().nonnegative().optional(),
    arithmeticVerdict: ArithmeticVerdictSchema.optional(),
    sizingFit: SizingFitSchema.optional(),
    grade: z.number().min(1).max(10).optional(),
    gradeBand: GradeBandSchema,
    evLossBB: z.number().nullable().optional(),
    /** only the touched dimensions (§7.4). */
    skillScores: z.record(z.string(), z.number()).optional(),
    errorCodes: z.array(z.string()).optional(),
    firedRuleIds: z.array(z.string()).optional(),
});
// ── EngineFacts (full record) ────────────────────────────────────────────────
export const EngineFactsSchema = z.object({
    v: z.literal(1),
    handId: z.string(),
    street: StreetSchema,
    decisionIndex: z.number().int().nonnegative(),
    context: z.object({
        gameType: GameTypeSchema,
        mode: ModeIdSchema,
        position: PositionSchema,
        positionClass: PositionClassSchema,
        playersInHand: z.number().int().positive(),
        icmPressure: IcmPressureSchema.optional(),
    }),
    math: z.object({
        pot: z.number().nonnegative(),
        toCall: z.number().nonnegative(),
        effectiveStack: z.number().nonnegative(),
        spr: z.number().nonnegative(),
        sprBucket: SprBucketSchema,
        potOdds: z.string().optional(),
        requiredEquityPct: z.number().optional(),
    }),
    hero: z.object({
        holeCards: z.tuple([CardIndexSchema, CardIndexSchema]),
        handClass: HandClassSchema,
        drawFlags: z.array(DrawFlagSchema),
        equity: EquityResultSchema,
        equityBucket: EquityBucketSchema,
        equityDelta: z.number().optional(),
        equityDeltaBucket: DeltaBucketSchema.optional(),
    }),
    board: z.object({
        cards: BoardSchema,
        textureClass: TextureClassSchema,
        streetEvent: BoardEventSchema.optional(),
    }),
    villain: z.object({
        seat: z.number().int().nonnegative(),
        archetypeId: ArchetypeIdSchema,
        imbalances: ImbalanceProfileSchema,
        rangeShape: RangeShapeSchema,
        bucketed: BucketedRangeSchema.optional(),
    }),
    heroPerceived: z.object({
        summary: z.array(PerceivedClassLabelSchema),
        capped: z.boolean(),
        image: HeroImageStateSchema.optional(),
    }),
    recommendation: RecommendedActionSchema,
    whatChanged: WhatChangedSchema.optional(),
    icm: IcmFactsSchema.optional(),
    userAction: UserActionSchema.optional(),
});
// ── FactsDigest (coach-facing projection) ──────────────────────────────────────
// Bucketed/enumerated conclusions ONLY - no hole cards, no card strings, no raw inputs
// (ARCHITECTURE.md §5). Its canonical serialization is the coach cache key.
export const FactsDigestSchema = z.object({
    v: z.literal(1),
    street: StreetSchema,
    context: z.object({
        gameType: GameTypeSchema,
        mode: ModeIdSchema,
        positionClass: PositionClassSchema,
        position: PositionSchema,
        playersInHand: z.number().int().positive(),
        icmPressure: IcmPressureSchema.optional(),
    }),
    sprBucket: SprBucketSchema,
    priceBucket: z
        .object({ potOdds: z.string(), requiredEquityPct: z.number() })
        .optional(),
    hero: z.object({
        handClass: HandClassSchema,
        drawFlags: z.array(DrawFlagSchema),
        equityBucket: EquityBucketSchema,
        equityDeltaBucket: DeltaBucketSchema.optional(),
    }),
    board: z.object({
        textureClass: TextureClassSchema,
        streetEvent: BoardEventSchema.optional(),
    }),
    villain: z.object({
        archetypeId: ArchetypeIdSchema,
        imbalances: ImbalanceSummarySchema,
        rangeShape: RangeShapeSchema,
    }),
    heroPerceived: z.object({
        summary: z.array(PerceivedClassLabelSchema),
        capped: z.boolean(),
    }),
    recommendation: z.object({
        action: ActionKindSchema,
        sizingIntent: SizingIntentSchema.optional(),
        firedRules: z.array(z.object({ id: z.string(), principle: z.string(), confidence: ConfidenceSchema })),
        alternatives: z.array(z.object({ action: ActionKindSchema, evGapBucket: EvGapBucketSchema })),
    }),
    userAction: z
        .object({
        action: ActionKindSchema,
        sizingIntent: SizingIntentSchema.optional(),
        arithmeticVerdict: ArithmeticVerdictSchema.optional(),
        sizingFit: SizingFitSchema.optional(),
        gradeBand: GradeBandSchema,
    })
        .optional(),
});
//# sourceMappingURL=facts.js.map